import React, { useEffect, useState } from 'react';
import { CalendarCheck, RefreshCw, AlertTriangle, CheckCircle, Clock, Send } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../common/ToastMsg';
import { cn } from '../../utils/tailwindUtils';

const STATUS_TONE = {
  requested: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  approved:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  arrived:   'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  no_show:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};
const OPEN = ['requested', 'approved'];

function fmtDate(d, t) {
  if (!d) return '';
  const day = new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return t ? `${day}, ${t}` : day;
}

/**
 * eMedHub appointment requests on an enquiry. Moving the enquiry to
 * Appointment Booked sends its profile to eMedHub automatically; this card
 * shows what happened to it and lets the rep retry, cancel or send it now.
 *
 * Renders nothing for hospitals without the eMedHub link (and no past
 * requests), so non-linked tenants see no change.
 */
export default function EmedhubAppointments({ enquiryId, enquiryStatus, converted, onChanged }) {
  const { fetch: apiFetch, create, patch } = useApiMethods();
  const [rows, setRows] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const resp = await apiFetch(`engage/enquiries/${enquiryId}/appointments/`);
    if (resp?.status === 'success') {
      setRows(resp.results || []);
      setEnabled(!!resp.link_enabled);
    }
  };

  // Reload when the stage changes: that is what creates or withdraws a request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [enquiryId, enquiryStatus]);

  if (!enabled && rows.length === 0) return null;

  const open = rows.find(r => OPEN.includes(r.status));

  const sendNow = async () => {
    setBusy('send');
    const resp = await create(`engage/enquiries/${enquiryId}/appointments/`, {});
    setBusy(null);
    if (resp?.status === 'success') {
      const latest = (resp.results || [])[0];
      ToastMsg(latest?.push_state === 'sent' ? 'success' : 'error',
        latest?.push_state === 'sent' ? 'Sent to eMedHub' : (latest?.last_error || 'Saved — will retry sending'));
      onChanged?.();
    } else {
      ToastMsg('error', resp?.message || 'Could not send');
    }
    load();
  };

  const retry = async (row) => {
    setBusy(row.id);
    const resp = await create(`engage/enquiries/appointments/${row.id}/retry/`, {});
    setBusy(null);
    ToastMsg(resp?.status === 'success' ? 'success' : 'error',
      resp?.status === 'success' ? 'Sent to eMedHub' : (resp?.message || 'Still failing'));
    load();
  };

  const cancel = async (row) => {
    if (!window.confirm('Cancel this appointment request in eMedHub?')) return;
    setBusy(row.id);
    const resp = await patch(`engage/enquiries/appointments/${row.id}/`, { action: 'cancel' });
    setBusy(null);
    if (resp?.status === 'success') { ToastMsg('success', 'Request cancelled'); load(); onChanged?.(); }
    else ToastMsg('error', resp?.message || 'Could not cancel');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <CalendarCheck className="w-3.5 h-3.5 text-indigo-500" /> eMedHub appointment
        </h3>
        {enabled && !open && !converted && (
          <button onClick={sendNow} disabled={busy === 'send'}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy === 'send' ? 'Sending…' : 'Send to eMedHub'}
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Moving this enquiry to Appointment Booked sends the patient's details to eMedHub as an
          appointment request for the front office.
        </p>
      )}

      <div className="space-y-2">
        {rows.slice(0, 3).map(r => (
          <div key={r.id} className="rounded-lg border border-gray-100 dark:border-gray-700 p-2.5 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                {r.doctor_name || r.speciality_name || 'Appointment request'}
              </span>
              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', STATUS_TONE[r.status])}>
                {r.status_display}
              </span>
            </div>
            <div className="mt-1 text-gray-600 dark:text-gray-300 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {fmtDate(r.appointment_date, r.appointment_time)}
              {r.doctor_name && r.speciality_name && <span className="text-gray-400">· {r.speciality_name}</span>}
            </div>
            {(r.uhid || r.token_number) && (
              <div className="mt-1 text-gray-500 dark:text-gray-400">
                {r.uhid && <>UHID {r.uhid}</>}{r.uhid && r.token_number && ' · '}{r.token_number && <>Token {r.token_number}</>}
              </div>
            )}
            <div className="mt-1.5 flex items-center justify-between gap-2">
              {r.push_state === 'sent' ? (
                <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> In eMedHub</span>
              ) : r.push_state === 'failed' ? (
                <span className="inline-flex items-center gap-1 text-red-600" title={r.last_error}>
                  <AlertTriangle className="w-3 h-3" /> {r.last_error || 'Not sent'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-600" title={r.last_error}>
                  <RefreshCw className="w-3 h-3" /> Waiting to send
                </span>
              )}
              {OPEN.includes(r.status) && (
                <span className="flex gap-2 shrink-0">
                  {r.push_state !== 'sent' && (
                    <button disabled={busy === r.id} onClick={() => retry(r)} className="text-indigo-600 hover:underline">Retry</button>
                  )}
                  {r.status === 'requested' && (
                    <button disabled={busy === r.id} onClick={() => cancel(r)} className="text-gray-500 hover:text-red-600">Cancel</button>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
